import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import AutoRedirect from "../components/Autologin";
import {
  getAppConfig,
  resolveAppNumberFromEnvironment,
} from "../config/appsConfig";
import { isMobile } from "react-device-detect";
import OptIn from "../components/Optin";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faNewspaper,
  faMobileAlt,
} from "@fortawesome/free-solid-svg-icons";
import heroImage from "../asset/5.webp";

const Home = () => {
  const user = useSelector((state) => state.user.user);
  const sessionToken = useSelector((state) => state.user.token);
  const isLoggedIn = sessionToken !== null;
  const userId = user?.user?.id;
  const apiPrefix = process.env.REACT_APP_API_PREFIX;
  const appNumber = useSelector((state) => state.app.appNumber);

  const optInStatus = user?.user?.optin;
  const username = user?.user?.username;
  const firstname = user?.user?.firstname;

  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  const resolvedAppNumber =
    appNumber ||
    resolveAppNumberFromEnvironment({
      hostname: window.location.hostname,
      port: window.location.port,
      search: window.location.search,
    });
  const appConfig = getAppConfig(resolvedAppNumber) || {};
  const desktopRedirectUrl = appConfig.desktopRedirectUrl || "";
  const organisation =
    appConfig.mobileOrganisationName || appConfig.organisationName || "";
  const guidePdfUrl =
    appConfig.guidePdfUrl || "/docs/guide-connexion-mobile-klesia.pdf";
  const contactEmail = appConfig.contactEmail || "klesia-pro@hapluspme.com";

  // Helper pour les styles App4
  const isApp4 = appNumber === 4;
  const isApp5 = appNumber === 5;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    if (token && !isMobile) {
      const newUrl = `${window.location.origin}${window.location.pathname}/${token}`;
      window.history.replaceState(null, null, newUrl);
    }
  }, []);

  const handleRedirect = async (toMobileApp) => {
    try {
      const response = await fetch(`${apiPrefix}autologin/token/${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`,
        },
      });

      if (!response.ok) throw new Error("Impossible de récupérer le token");
      const data = await response.json();
      const token = data.token;

      setShowModal(false);
      if (toMobileApp) {
        window.location.href = `https://app.reducce.fr/autologin/${token}?organisation=${organisation}`;
      } else {
        window.location.href = `${desktopRedirectUrl}${token}`;
      }
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la redirection. Veuillez réessayer.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div
        className="text-white py-16 md:py-20 lg:py-24 px-4 relative bg-cover bg-center bg-no-repeat min-h-[500px] md:min-h-[600px]"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        {/* Overlay coloré pour améliorer la lisibilité */}
        <div
          className={`absolute inset-0 ${
            isApp4
              ? "hero-overlay-app4"
              : "bg-gradient-to-br from-theme-primary/40 to-theme-primary-active/60"
          }`}
        ></div>

        <div className="hero-content max-w-6xl mx-auto text-center relative z-10">
          {isLoggedIn && (
            <h2 className="text-2xl font-semibold mb-4 text-white text-shadow-sm">
              Bonjour {firstname}
            </h2>
          )}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 leading-tight text-white text-shadow-md">
            Bienvenue sur votre <br className="hidden md:block" />{" "}
            {isApp5 ? "Espace Avantages" : "Comité d'entreprise"} !
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-white mb-8 md:mb-10 max-w-2xl mx-auto text-shadow-sm px-4">
            Découvrez des offres exclusives sur vos achats du quotidien.
          </p>

          {!isMobile && (
            <AutoRedirect
              redirectUrl={desktopRedirectUrl}
              isHero={true}
              appNumber={appNumber}
            />
          )}

          {isMobile && (
            <button
              onClick={() => setShowModal(true)}
              className="bg-white/95 backdrop-blur-sm text-theme-primary hover:bg-white hover:text-theme-primary-active text-base md:text-lg px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 active:shadow-md"
            >
              <i className="fas fa-mobile-alt mr-2"></i>
              Accéder à mes offres
            </button>
          )}

          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="card-theme max-w-md w-full text-center relative">
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  &times;
                </button>
                <div className="mb-6">
                  <i className="fas fa-mobile-alt text-4xl text-theme-accent mb-4"></i>
                  <h2 className="text-2xl font-bold text-theme-primary mb-4">
                    Où souhaitez-vous aller ?
                  </h2>
                  <p className="text-gray-600">
                    Souhaitez-vous utiliser l'application mobile ou la version
                    web ?
                  </p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={() => handleRedirect(true)}
                    className="btn-theme w-full"
                  >
                    <i className="fas fa-mobile-alt mr-2"></i>
                    Application mobile
                  </button>
                  <button
                    onClick={() => handleRedirect(false)}
                    className="btn-theme-secondary w-full"
                  >
                    <i className="fas fa-desktop mr-2"></i>
                    Version web
                  </button>
                </div>
                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cards Section */}
      <div
        className={`py-16 md:py-20 lg:py-24 px-4 md:px-6 ${
          isApp4
            ? "services-section-app4"
            : "bg-gradient-to-br from-slate-50 to-slate-100"
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-theme-primary mb-4 md:mb-5">
              Nos Services
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4 leading-relaxed">
              Découvrez tous les services disponibles pour votre{" "}
              {isApp5 ? "espace avantages" : "comité d'entreprise"}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12 items-stretch">
            <div className="card-modern flex flex-col">
              <div className="icon-modern mb-6">
                <i className="fas fa-rocket"></i>
              </div>
              <h3 className="card-title-modern mb-4">Découvrir les offres</h3>
              <p className="card-description-modern mb-6 flex-grow">
                Accédez directement à toutes les offres et bons plans
                disponibles pour votre{" "}
                {isApp5 ? "espace avantages" : "comité d'entreprise"}. Découvrez
                des réductions exclusives sur vos achats du quotidien.
              </p>
              <div className="mt-auto">
                <AutoRedirect redirectUrl={desktopRedirectUrl} />
              </div>
            </div>

            <div className="card-modern flex flex-col">
              <div className="icon-modern mb-6">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <h3 className="card-title-modern mb-4">Mon compte</h3>
              <p className="card-description-modern mb-6 flex-grow">
                Bienvenue dans votre espace personnel. Ici, vous pouvez gérer
                vos informations personnelles et les mettre à jour, de manière
                simple et sécurisée.
              </p>
              <Link to="/Profil" className="block mt-auto">
                <button className="btn-modern w-full">
                  <i className="fas fa-user mr-2"></i>
                  Mon compte
                </button>
              </Link>
            </div>

            <div className="card-modern flex flex-col">
              <div className="icon-modern mb-6">
                <FontAwesomeIcon icon={faMobileAlt} />
              </div>
              <h3 className="card-title-modern mb-4">
                Se connecter sur mobile
              </h3>
              <p className="card-description-modern mb-6 flex-grow">
                Besoin d'aide pour vous connecter à l'application mobile ?
                Téléchargez notre guide rapide pour vous accompagner pas à pas.
              </p>
              <a href={guidePdfUrl} download className="block mt-auto">
                <button className="btn-modern w-full">
                  <i className="fas fa-download mr-2"></i>
                  Télécharger le guide
                </button>
              </a>
            </div>

            <div className="card-modern flex flex-col">
              <div className="icon-modern mb-6">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h3 className="card-title-modern mb-4">Contact</h3>
              <p className="card-description-modern mb-6 flex-grow">
                Pour toute réclamation sur la billetterie ou le paiement,
                contactez Ski Loisirs Diffusion au
                <br className="hidden md:block" /> 04 72 40 28 28 ou par mail à
                serviceclient@reducce.fr. Pour toute autre demande :
              </p>
              <a href={`mailto:${contactEmail}`} className="block mt-auto">
                <button className="btn-modern w-full">
                  <i className="fas fa-envelope mr-2"></i>
                  Contactez nous !
                </button>
              </a>
            </div>

            <div className="card-modern flex flex-col">
              <div className="icon-modern mb-6">
                <FontAwesomeIcon icon={faNewspaper} />
              </div>
              <h3 className="card-title-modern mb-4">Newsletter</h3>
              <p className="card-description-modern mb-6 flex-grow">
                Ne manquez rien ! Inscrivez-vous pour recevoir les bons plans de
                la semaine et des offres exclusives directement dans votre boîte
                mail.
              </p>
              <div className="mt-auto">
                {optInStatus !== undefined && (
                  <div className="btn-modern flex items-center justify-center w-full">
                    <OptIn username={username} optIn={optInStatus} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
